import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Loader2, X } from 'lucide-react';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui';
import { AuditTimelineItem } from '@/components/audit/AuditTimelineItem';
import { DataTablePagination } from '@/components/data-table/Pagination';
import { useGetAuditLogs } from '@/services';
import { getSessionDisplayTitle } from '@/utils';
import { cn } from '@/lib/utils';
import {
	AUDIT_LOG_ACTION_LABEL,
	AUDIT_LOG_ACTIONS_BY_CATEGORY,
	AUDIT_LOG_CATEGORIES,
	AUDIT_LOG_CATEGORY_LABEL,
	EMITTED_AUDIT_LOG_ACTIONS,
	dateInputToIsoEnd,
	dateInputToIsoStart,
} from '@/utils/auditLogDisplay';

const FILTER_ALL = '__all__';
const TIMELINE_PAGE_SIZE = 50;
const POLLING_INTERVAL_MS = 30_000;

const SCROLL_CLASS = cn(
	'overflow-y-auto overscroll-contain scroll-smooth [scrollbar-gutter:stable]',
	'[scrollbar-width:thin] [scrollbar-color:theme(colors.zinc.300)_transparent]',
	'[&::-webkit-scrollbar]:w-1.5',
	'[&::-webkit-scrollbar-track]:bg-transparent',
	'[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-zinc-300',
	'hover:[&::-webkit-scrollbar-thumb]:bg-zinc-400',
);

type SessionAuditModalProps = {
	isOpen: boolean;
	onClose: () => void;
	session: Sessions.Session | null;
};

const SessionAuditModal = ({
	isOpen,
	onClose,
	session,
}: SessionAuditModalProps) => {
	const [page, setPage] = useState(1);
	const [categoryFilter, setCategoryFilter] = useState(FILTER_ALL);
	const [actionFilter, setActionFilter] = useState(FILTER_ALL);
	const [fromDate, setFromDate] = useState('');
	const [toDate, setToDate] = useState('');

	const sessionId = session?.id ?? '';

	useEffect(() => {
		if (!isOpen) return;
		setPage(1);
		setCategoryFilter(FILTER_ALL);
		setActionFilter(FILTER_ALL);
		setFromDate('');
		setToDate('');
	}, [isOpen, sessionId]);

	useEffect(() => {
		setPage(1);
	}, [categoryFilter, actionFilter, fromDate, toDate]);

	const queryParams = useMemo<AuditLogs.AuditLogListParams>(() => {
		const params: AuditLogs.AuditLogListParams = {
			page,
			limit: TIMELINE_PAGE_SIZE,
			sort_direction: 'desc',
			session_id: sessionId,
		};

		if (categoryFilter !== FILTER_ALL) {
			params.category = categoryFilter as AuditLogs.AuditLogCategory;
		}
		if (actionFilter !== FILTER_ALL) {
			params.action = actionFilter as AuditLogs.AuditLogAction;
		}
		if (fromDate) {
			params.from = dateInputToIsoStart(fromDate);
		}
		if (toDate) {
			params.to = dateInputToIsoEnd(toDate);
		}

		return params;
	}, [page, sessionId, categoryFilter, actionFilter, fromDate, toDate]);

	const { data, isLoading, isError, isFetching } = useGetAuditLogs(
		queryParams,
		{
			enabled: isOpen && !!sessionId,
			refetchInterval: isOpen ? POLLING_INTERVAL_MS : false,
		},
	);

	const events = data?.data ?? [];
	const totalItems = data?.pagination?.total ?? 0;
	const sessionTitle = session ? getSessionDisplayTitle(session) : 'Sessão';

	const filteredActionOptions = useMemo(() => {
		if (categoryFilter === FILTER_ALL) return EMITTED_AUDIT_LOG_ACTIONS;
		return (
			AUDIT_LOG_ACTIONS_BY_CATEGORY[
				categoryFilter as AuditLogs.AuditLogCategory
			] ?? []
		);
	}, [categoryFilter]);

	useEffect(() => {
		if (
			actionFilter !== FILTER_ALL &&
			!filteredActionOptions.includes(
				actionFilter as AuditLogs.AuditLogAction,
			)
		) {
			setActionFilter(FILTER_ALL);
		}
	}, [actionFilter, filteredActionOptions]);

	useEffect(() => {
		if (!isOpen) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
		};

		document.body.style.overflow = 'hidden';
		window.addEventListener('keydown', onKeyDown);

		return () => {
			document.body.style.overflow = 'auto';
			window.removeEventListener('keydown', onKeyDown);
		};
	}, [isOpen, onClose]);

	if (!isOpen || !session) return null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
				aria-hidden
			/>
			<div
				className="animate-in zoom-in-95 relative flex h-[75vh] w-[75vw] max-w-[75vw] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200"
				role="dialog"
				aria-modal="true"
				aria-labelledby="session-audit-modal-title"
			>
				<div className="flex shrink-0 items-start justify-between gap-4 border-b border-stone-100 px-8 py-6">
					<div className="flex min-w-0 items-center gap-4">
						<div className="flex size-12 shrink-0 items-center justify-center rounded-xl border border-stone-200 bg-stone-50 text-stone-700">
							<FileText
								className="size-5"
								strokeWidth={2.25}
								aria-hidden
							/>
						</div>
						<div className="min-w-0">
							<h2
								id="session-audit-modal-title"
								className="font-albert truncate text-xl font-bold text-stone-900"
							>
								{sessionTitle}
							</h2>
							<p className="text-sm text-stone-500">
								Linha do tempo de auditoria da sessão
							</p>
						</div>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="shrink-0 text-stone-400 transition-colors hover:text-stone-600"
						aria-label="Fechar"
					>
						<X size={20} />
					</button>
				</div>

				<div className="shrink-0 border-b border-stone-100 bg-stone-50/60 px-8 py-4">
					<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
						<div>
							<span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-stone-500">
								Categoria
							</span>
							<Select
								value={categoryFilter}
								onValueChange={setCategoryFilter}
							>
								<SelectTrigger className="h-10 w-full border-stone-200 bg-white text-left text-sm">
									<SelectValue>
										{categoryFilter === FILTER_ALL
											? 'Todas'
											: AUDIT_LOG_CATEGORY_LABEL[
													categoryFilter as AuditLogs.AuditLogCategory
											  ]}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={FILTER_ALL}>
										Todas
									</SelectItem>
									{AUDIT_LOG_CATEGORIES.map((category) => (
										<SelectItem
											key={category}
											value={category}
										>
											{AUDIT_LOG_CATEGORY_LABEL[category]}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div>
							<span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-stone-500">
								Ação
							</span>
							<Select
								value={actionFilter}
								onValueChange={setActionFilter}
							>
								<SelectTrigger className="h-10 w-full border-stone-200 bg-white text-left text-sm">
									<SelectValue>
										{actionFilter === FILTER_ALL
											? 'Todas'
											: AUDIT_LOG_ACTION_LABEL[
													actionFilter as AuditLogs.AuditLogAction
											  ]}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={FILTER_ALL}>
										Todas
									</SelectItem>
									{filteredActionOptions.map((action) => (
										<SelectItem key={action} value={action}>
											{AUDIT_LOG_ACTION_LABEL[action]}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div>
							<span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-stone-500">
								De
							</span>
							<input
								type="date"
								value={fromDate}
								onChange={(event) =>
									setFromDate(event.target.value)
								}
								className="h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 outline-none transition focus:border-stone-400"
							/>
						</div>

						<div>
							<span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-stone-500">
								Até
							</span>
							<input
								type="date"
								value={toDate}
								onChange={(event) =>
									setToDate(event.target.value)
								}
								className="h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 outline-none transition focus:border-stone-400"
							/>
						</div>
					</div>
				</div>

				<div className={cn('min-h-0 flex-1 px-8 py-6', SCROLL_CLASS)}>
					{isLoading || (isFetching && events.length === 0) ? (
						<div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-stone-200 bg-stone-50 px-4 py-16 text-sm text-stone-500">
							<Loader2
								className="size-4 animate-spin"
								aria-hidden
							/>
							Carregando auditoria…
						</div>
					) : isError ? (
						<p className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-10 text-center text-sm text-rose-800">
							Não foi possível carregar os eventos de auditoria.
						</p>
					) : events.length === 0 ? (
						<p className="rounded-xl border border-dashed border-stone-200 bg-stone-50 px-4 py-12 text-center text-sm text-stone-500">
							Nenhum evento encontrado para os filtros
							selecionados.
						</p>
					) : (
						<ul className="relative">
							{events.map((entry, index) => (
								<AuditTimelineItem
									key={entry.id}
									entry={entry}
									isLast={index === events.length - 1}
								/>
							))}
						</ul>
					)}
				</div>

				{totalItems > TIMELINE_PAGE_SIZE ? (
					<div className="flex shrink-0 items-center justify-between border-t border-stone-100 px-8 py-4">
						<p className="text-xs text-stone-500">
							{totalItems} evento{totalItems === 1 ? '' : 's'}
							{isFetching ? (
								<span className="ml-2 inline-flex items-center gap-1 text-stone-400">
									<Loader2
										className="size-3 animate-spin"
										aria-hidden
									/>
									Atualizando…
								</span>
							) : null}
						</p>
						<DataTablePagination
							page={page}
							pageSize={TIMELINE_PAGE_SIZE}
							totalItems={totalItems}
							onPageChange={setPage}
						/>
					</div>
				) : isFetching ? (
					<div className="flex shrink-0 items-center justify-end border-t border-stone-100 px-8 py-3">
						<span className="inline-flex items-center gap-1.5 text-xs text-stone-400">
							<Loader2
								className="size-3 animate-spin"
								aria-hidden
							/>
							Atualizando…
						</span>
					</div>
				) : null}
			</div>
		</div>,
		document.body,
	);
};

export { SessionAuditModal };
